import React, { useState, useEffect } from 'react';
import { Shield, Plus, RefreshCw, Search, X, Eye, Trash2, CheckCircle, XCircle, AlertCircle, Copy, ChevronRight, Filter, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, ShieldCheck } from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };
const inputClass = "w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors";
const inputBorder = { border: '1px solid rgba(99, 102, 241, 0.15)' };
const labelClass = "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5";

const PEChainManagement = () => {
  const [chains, setChains] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [selectedChain, setSelectedChain] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    pe_id: '',
    is_active: '',
    tm1_id: '',
    tm2_id: '',
    tmd_id: ''
  });
  
  const [formData, setFormData] = useState({
    chain_name: '',
    pe_id: '',
    tm1_id: '',
    tm2_id: '',
    tmd_id: '',
    effective_from: new Date().toISOString().slice(0, 16),
    effective_to: ''
  });

  useEffect(() => {
    fetchData();
    fetchEntities();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchText.trim()) params.append('search', searchText.trim());
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/admin/compliance/pe-tm-chains?${params}`);
      setChains(response.data.data || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch chains:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    try {
      const response = await api.get('/admin/entities');
      setEntities(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    }
  };

  const handleCreateChain = async () => {
    try {
      await api.post('/admin/compliance/pe-tm-chains', formData);
      setDialogOpen(false);
      setFormData({ chain_name: '', pe_id: '', tm1_id: '', tm2_id: '', tmd_id: '', effective_from: new Date().toISOString().slice(0, 16), effective_to: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to create chain:', error);
    }
  };

  const handleValidateChain = async (chainId) => {
    try {
      const response = await api.post(`/admin/compliance/pe-tm-chains/${chainId}/validate`);
      setValidationResult(response.data.data);
      setValidationDialogOpen(true);
    } catch (error) {
      console.error('Failed to validate chain:', error);
    }
  };

  const handleCopyHash = async (hash) => {
    navigator.clipboard.writeText(hash);
  };

  const handleViewDetails = (chain) => {
    setSelectedChain(chain);
    setDetailsDialogOpen(true);
  };

  const handleActivateChain = async (chainId) => {
    try { await api.post(`/admin/compliance/pe-tm-chains/${chainId}/activate`); fetchData(); } catch (error) { console.error('Failed to activate chain:', error); }
  };

  const handleDeactivateChain = async (chainId) => {
    try { await api.post(`/admin/compliance/pe-tm-chains/${chainId}/deactivate`); fetchData(); } catch (error) { console.error('Failed to deactivate chain:', error); }
  };

  const handleDeleteChain = async (chainId) => {
    if (window.confirm('Are you sure you want to delete this PE-TM chain?')) {
      try { await api.delete(`/admin/compliance/pe-tm-chains/${chainId}`); fetchData(); } catch (error) { console.error('Failed to delete chain:', error); }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  };

  const getStatusStyle = (chain) => {
    const status = chain.computed_status || chain.status || (chain.is_active ? 'ACTIVE' : 'INACTIVE');
    if (status === 'ACTIVE') return { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' };
    if (status === 'EXPIRED') return { background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)' };
    return { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' };
  };

  const activeFilterCount = [searchText, filters.pe_id, filters.tm1_id, filters.tm2_id, filters.tmd_id, filters.is_active].filter(v => v && v.trim() !== '').length;

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            PE-TM Chain Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">SMPP template compliance chain management</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : ''}</span>
          <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm" style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={() => setDialogOpen(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            <Plus className="w-3.5 h-3.5" /> Create Chain
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#6366f1' }} />
        <p className="text-xs text-gray-600">PE-TM chains ensure SMS template compliance for SMPP routes. Formats: PE → TMD (direct), PE → TM1 → TMD, PE → TM1 → TM2 → TMD.</p>
      </div>

      {/* Collapsible Filter Panel */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <button type="button" onClick={() => setFiltersOpen(!filtersOpen)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50 transition-colors cursor-pointer select-none" style={{ background: 'rgba(99, 102, 241, 0.02)' }}>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#6366f1' }} />
            <span className="text-sm font-semibold text-gray-700">Search & Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white rounded-full" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>{activeFilterCount}</span>
            )}
          </div>
          {filtersOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {filtersOpen && (
          <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <input className={inputClass} style={inputBorder} placeholder="Search by Chain Name, PE ID, TMD ID, Hash..." value={searchText} onChange={(e) => setSearchText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') fetchData(); }} />
                </div>
                <button onClick={fetchData} className="h-9 px-4 rounded-lg text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  <Search className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                <div><label className={labelClass}>PE ID</label><input className={inputClass} style={inputBorder} value={filters.pe_id} onChange={(e) => setFilters({...filters, pe_id: e.target.value})} onKeyDown={(e) => { if (e.key === 'Enter') fetchData(); }} /></div>
                <div><label className={labelClass}>TM1 ID</label><input className={inputClass} style={inputBorder} value={filters.tm1_id} onChange={(e) => setFilters({...filters, tm1_id: e.target.value})} onKeyDown={(e) => { if (e.key === 'Enter') fetchData(); }} /></div>
                <div><label className={labelClass}>TM2 ID</label><input className={inputClass} style={inputBorder} value={filters.tm2_id} onChange={(e) => setFilters({...filters, tm2_id: e.target.value})} onKeyDown={(e) => { if (e.key === 'Enter') fetchData(); }} /></div>
                <div><label className={labelClass}>TMD ID</label><input className={inputClass} style={inputBorder} value={filters.tmd_id} onChange={(e) => setFilters({...filters, tmd_id: e.target.value})} onKeyDown={(e) => { if (e.key === 'Enter') fetchData(); }} /></div>
                <div><label className={labelClass}>Status</label>
                  <select className={inputClass} style={inputBorder} value={filters.is_active} onChange={(e) => setFilters({...filters, is_active: e.target.value})}>
                    <option value="">All</option><option value="true">Active</option><option value="false">Inactive</option>
                  </select>
                </div>
                <div className="flex items-end gap-1">
                  <button onClick={fetchData} className="h-9 px-3 rounded-lg text-xs font-medium text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>Apply</button>
                  <button onClick={() => { setSearchText(''); setFilters({ pe_id: '', is_active: '', tm1_id: '', tm2_id: '', tmd_id: '' }); setTimeout(fetchData, 0); }} className="h-9 px-3 rounded-lg text-xs font-medium" style={{ color: '#6b7280', background: 'rgba(107, 114, 128, 0.06)', border: '1px solid rgba(107, 114, 128, 0.15)' }}>Clear</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <span className="text-xs font-medium text-gray-500">{chains.length} chain{chains.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="p-16 flex flex-col items-center">
            <RefreshCw className="w-8 h-8 animate-spin mb-3" style={{ color: '#6366f1' }} />
            <p className="text-sm text-gray-500">Loading chains...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider w-10">No</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Chain Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">PE ID</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">TM Chain</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">TMD ID</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Hash</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Effective</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {chains.length === 0 ? (
                  <tr><td colSpan="9" className="px-6 py-12 text-center"><Shield className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No chains found</p></td></tr>
                ) : chains.map((chain, index) => (
                  <tr key={chain.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                    <td className="px-4 py-3 text-xs text-gray-400">{index + 1}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-gray-800">{chain.chain_name}</span>
                      {chain.is_auto_generated && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)' }}>Auto</span>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600">{chain.pe_id}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600">
                      {chain.tm1_id ? (chain.tm2_id ? <>{chain.tm1_id} <ChevronRight className="w-3 h-3 inline text-gray-400" /> {chain.tm2_id}</> : chain.tm1_id) : <span className="italic text-gray-400">Direct</span>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600">{chain.tmd_id}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] font-mono text-gray-500">{chain.hash_value?.substring(0, 12)}...</span>
                        <button onClick={() => handleCopyHash(chain.hash_value)} className="w-5 h-5 rounded flex items-center justify-center hover:bg-gray-100"><Copy className="w-3 h-3 text-gray-400" /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[10px] text-gray-500">{formatDate(chain.effective_from)}</div>
                      {chain.effective_to && <div className="text-[10px] text-gray-400">→ {formatDate(chain.effective_to)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusStyle(chain)}>
                        {chain.computed_status || chain.status || (chain.is_active ? 'ACTIVE' : 'INACTIVE')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => handleViewDetails(chain)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="View Details"><Eye className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
                        <button onClick={() => handleValidateChain(chain.id)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="Validate"><ShieldCheck className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
                        <button onClick={() => chain.is_active ? handleDeactivateChain(chain.id) : handleActivateChain(chain.id)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100" title={chain.is_active ? 'Deactivate' : 'Activate'}>
                          {chain.is_active ? <ToggleRight className="w-4 h-4 text-green-500" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                        </button>
                        <button onClick={() => handleDeleteChain(chain.id)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Chain Modal */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}><Plus className="w-3.5 h-3.5 text-white" /></div>
                Create PE-TM Chain
              </h3>
              <button onClick={() => setDialogOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div><label className={labelClass}>Chain Name</label><input className={inputClass} style={inputBorder} placeholder="e.g., Banking-OTP-Primary-Chain" value={formData.chain_name} onChange={(e) => setFormData({...formData, chain_name: e.target.value})} maxLength={100} /></div>
              <div><label className={labelClass}>PE ID *</label><input className={inputClass} style={inputBorder} placeholder="10-25 digits" value={formData.pe_id} onChange={(e) => setFormData({...formData, pe_id: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelClass}>TM1 ID (Optional)</label><input className={inputClass} style={inputBorder} placeholder="10-25 digits" value={formData.tm1_id} onChange={(e) => setFormData({...formData, tm1_id: e.target.value})} /></div>
                <div><label className={labelClass}>TM2 ID (Optional)</label><input className={inputClass} style={inputBorder} placeholder="10-25 digits" value={formData.tm2_id} onChange={(e) => setFormData({...formData, tm2_id: e.target.value})} /></div>
              </div>
              <div><label className={labelClass}>TMD ID *</label><input className={inputClass} style={inputBorder} placeholder="10-25 digits" value={formData.tmd_id} onChange={(e) => setFormData({...formData, tmd_id: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelClass}>Effective From</label><input type="datetime-local" className={inputClass} style={inputBorder} value={formData.effective_from} onChange={(e) => setFormData({...formData, effective_from: e.target.value})} /></div>
                <div><label className={labelClass}>Effective To (Optional)</label><input type="datetime-local" className={inputClass} style={inputBorder} value={formData.effective_to} onChange={(e) => setFormData({...formData, effective_to: e.target.value})} /></div>
              </div>
              <div className="rounded-lg p-3 mt-2" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Raw Input Preview</span>
                <p className="text-xs font-mono text-gray-700 mt-1">{formData.pe_id || 'PEID'}{formData.tm1_id ? ',' + formData.tm1_id : ''}{formData.tm2_id ? ',' + formData.tm2_id : ''},{formData.tmd_id || 'TMDID'}</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setDialogOpen(false)} className="px-4 py-2 rounded-xl text-xs font-medium" style={{ color: '#6b7280', background: 'rgba(107, 114, 128, 0.06)', border: '1px solid rgba(107, 114, 128, 0.15)' }}>Cancel</button>
                <button onClick={handleCreateChain} className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Result Modal */}
      {validationDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900">Validation Result</h3>
              <button onClick={() => setValidationDialogOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            {validationResult && (
              <div className="p-6 space-y-3">
                <div className="rounded-xl p-3 flex items-center gap-2" style={validationResult.is_valid ? { background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' } : { background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                  {validationResult.is_valid ? <CheckCircle className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                  <span className="text-sm font-semibold" style={{ color: validationResult.is_valid ? '#059669' : '#dc2626' }}>Chain is {validationResult.is_valid ? 'VALID' : 'INVALID'}</span>
                </div>
                <div className="text-xs text-gray-500">Validated at: {formatDate(validationResult.validated_at)}</div>
                {validationResult.validation_errors?.length > 0 && (
                  <div className="rounded-lg p-3" style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                    <span className="text-[10px] font-bold text-red-400 uppercase">Errors</span>
                    <p className="text-xs text-red-700 mt-1">{validationResult.validation_errors.join(', ')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chain Details Modal */}
      {detailsDialogOpen && selectedChain && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900">Chain Details</h3>
              <button onClick={() => setDetailsDialogOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-3">
              {[
                ['Chain ID', selectedChain.id],
                ['Chain Name', selectedChain.chain_name],
                ['PE ID', selectedChain.pe_id],
                ['Chain Format', selectedChain.tm1_id ? (selectedChain.tm2_id ? 'PE → TM1 → TM2 → TMD' : 'PE → TM1 → TMD') : 'PE → TMD (Direct)'],
                ...(selectedChain.tm1_id ? [['TM1 ID', selectedChain.tm1_id]] : []),
                ...(selectedChain.tm2_id ? [['TM2 ID', selectedChain.tm2_id]] : []),
                ['TMD ID', selectedChain.tmd_id],
                ['Created At', formatDate(selectedChain.created_at)],
                ...(selectedChain.approved_at ? [['Approved At', formatDate(selectedChain.approved_at)]] : []),
              ].map(([label, value], i) => (
                <div key={i} className="flex items-start justify-between py-2" style={i > 0 ? { borderTop: '1px solid rgba(99, 102, 241, 0.05)' } : {}}>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</span>
                  <span className="text-xs font-mono text-gray-800 text-right max-w-[60%]">{value}</span>
                </div>
              ))}
              <div className="flex items-start justify-between py-2" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.05)' }}>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Hash</span>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-mono text-gray-600 break-all max-w-[200px]">{selectedChain.hash_value}</span>
                  <button onClick={() => handleCopyHash(selectedChain.hash_value)} className="w-5 h-5 rounded flex items-center justify-center hover:bg-gray-100 flex-shrink-0"><Copy className="w-3 h-3 text-gray-400" /></button>
                </div>
              </div>
              <div className="flex items-center justify-between py-2" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.05)' }}>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={selectedChain.is_active ? { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' } : { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                  {selectedChain.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PEChainManagement;
